import { isRejectedWithValue } from '@reduxjs/toolkit';
import type { Middleware } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import { NO_ACCESS_TOKEN } from '@/store/api/baseApi';

import { HttpStatus } from '@/types/httpStatus';

// These endpoints act on a negotiation's current terms and handle their own 409 with the
// TermsChangedWarning (see useTermsChangedNotice), so the server's generic conflict text is not
// shown on top of it. Every other status still reaches the global handler.
const ENDPOINTS_HANDLING_OWN_CONFLICT = new Set([
  'acceptProposal',
  'counterProposal',
]);

// A 404 from these endpoints does not raise the global snackbar. The set is keyed by endpoint, so
// it applies to every caller. For getProjectById the project details page shows its own "not
// available" panel for a refused draft and a missing id (both 404s since RME-1480). Its other
// callers read the project of an offer, a trade or the dashboard's own list, where a 404 means only
// a project deleted mid-session; they show nothing for it.
const ENDPOINTS_HANDLING_OWN_NOT_FOUND = new Set(['getProjectById']);

const getEndpointName = (action: unknown): string | undefined =>
  (action as { meta?: { arg?: { endpointName?: string } } }).meta?.arg
    ?.endpointName;

const isQuery = (action: unknown): boolean =>
  (action as { meta?: { arg?: { type?: string } } }).meta?.arg?.type ===
  'query';

let errorHandlerFn: ((error: FetchBaseQueryError) => void) | null = null;

export const setErrorHandler = (
  handler: (error: FetchBaseQueryError) => void
) => {
  errorHandlerFn = handler;
};

// RTK Query middleware for global error handling
export const errorLoggerMiddleware: Middleware = () => next => action => {
  if (isRejectedWithValue(action)) {
    const payload = action.payload as FetchBaseQueryError;

    // Only handle if it's an API error with a status code
    if (payload && typeof payload === 'object' && 'status' in payload) {
      const status = payload.status;

      // Skip 401 (baseApi forces re-login) and 403 (permission denied — the
      // view degrades silently rather than surfacing a snackbar or logging out).
      const endpointName = getEndpointName(action) ?? '';
      const isHandledConflict =
        status === HttpStatus.CONFLICT &&
        ENDPOINTS_HANDLING_OWN_CONFLICT.has(endpointName);
      const isHandledNotFound =
        status === HttpStatus.NOT_FOUND &&
        ENDPOINTS_HANDLING_OWN_NOT_FOUND.has(endpointName);
      // No token could be had for a reason that may pass; no request was sent. A polling
      // query tries again on its next tick, so it stays quiet. A mutation is an action the
      // user took, so it still reports, with the friendly message baseApi attaches.
      const isQuietNoAccessToken =
        payload.status === 'CUSTOM_ERROR' &&
        payload.error === NO_ACCESS_TOKEN &&
        isQuery(action);

      if (
        status !== HttpStatus.UNAUTHORIZED &&
        status !== HttpStatus.FORBIDDEN &&
        !isHandledConflict &&
        !isHandledNotFound &&
        !isQuietNoAccessToken &&
        errorHandlerFn
      ) {
        errorHandlerFn(payload);
      }
    }
  }

  return next(action);
};
